import {
  APP_ROUTES_APP_NURSE_REQUISITIONS,
  APP_ROUTES_APP_PHARMACIST_REQUISITIONS,
  APP_ROUTES_APP_PHARMACIST_REQUISITIONS_NEW,
  APP_ROUTES_APP_REQUISITIONS_NEW
} from '@/src/constants/routes'
import { useProfile, useRequisitions, useUnit } from '@hook'
import { DropDownIcon, FilterIcon, TriDot } from '@icons'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@modal'
import { ColumnDef } from '@tanstack/react-table'
import { RequisitionData, RequisitionItem, RequisitionVariant } from '@type/requisitions.type'
import { Badge, DropdownBtn, Table2, TitleHeader } from '@ui'
import { getError, getIncludedData, showToast } from '@util'
import { Box, Text } from 'grommet'
import { Checkmark } from 'grommet-icons'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../../store/hooks'
import { ProfileUser } from '../../../../types/profile.type'
import { getRequisitionItems, statusLabel } from './requisitions.helpers'

type RequisitionDirection = 'outbound' | 'inbound'

const statusOptions = [
  { label: 'All Status', value: 'all' },
  { label: 'Pending Review', value: 'pending_review' },
  { label: 'Approved', value: 'approved' },
  { label: 'Partial Approval', value: 'partially_approved' },
  { label: 'Declined', value: 'declined' },
  { label: 'Cancelled', value: 'cancelled' }
]

const requisitionTabs: Array<{ label: string; value: RequisitionDirection }> = [
  { label: 'Outbound', value: 'outbound' },
  { label: 'Inbound', value: 'inbound' }
]

const productSummary = (items?: RequisitionItem[]) => {
  if (!items?.length) return '-'

  const maxNameLength = 25

  const displayNames = items
    .map(item => {
      const name = item.generic_name?.trim()
      const strength = item.strength?.trim()
      if (!name) return ''
      const fullName = strength ? `${name} ${strength}` : name
      return fullName.length > maxNameLength ? `${fullName.substring(0, maxNameLength)}...` : fullName
    })
    .filter((name): name is string => !!name && name.length > 0)

  if (!displayNames.length) return `${items.length} drug${items.length > 1 ? 's' : ''}`
  if (displayNames.length === 1) return displayNames[0]
  if (displayNames.length === 2) return `${displayNames[0]}, ${displayNames[1]}`

  return `${displayNames[0]}, ${displayNames[1]} & ${displayNames.length - 2} others`
}

type RequisitionsProps = {
  /** Comes from the route, which is guarded by the matching side-nav permission. */
  variant: RequisitionVariant
}

const Requisitions = ({ variant }: RequisitionsProps) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { openModal } = useModalStore()

  /** Nurse requisitions are outbound-only: no direction tabs. */
  const isNurseView = variant === 'nurse'

  const currentStaffId = useAppSelector(state => state.user.user?.authable_id)
  const { included: profileIncluded, profile } = useProfile({ isLoggedIn: !!currentStaffId })

  const currentUnitId = useMemo(() => {
    const userIncludedId = profile?.relationships?.user?.data?.id
    if (!userIncludedId) return ''
    const userIncluded = profileIncluded.find(
      (item): item is ProfileUser => item.type === 'user' && item.id === userIncludedId
    )
    const unitId = userIncluded?.attributes?.current_unit_id
    return unitId === undefined || unitId === null ? '' : String(unitId)
  }, [profile?.relationships?.user?.data?.id, profileIncluded])

  // Nurse requisitions are always outbound (no tabs)
  const tab = isNurseView ? 'outbound' : ((searchParams.get('tab') || 'outbound') as RequisitionDirection)
  const status = searchParams.get('status') || 'all'
  const unitId = searchParams.get('unit_id') || ''
  const query = searchParams.get('query') || ''
  const page = Number(searchParams.get('page') || 1)
  const limit = Number(searchParams.get('limit') || 20)

  const { unitList, unitsIsLoading } = useUnit({ page: 1, limit: 1000 })

  const [fulfillingId, setFulfillingId] = useState('')
  const [cancellingId, setCancellingId] = useState('')

  const {
    fulfillRequisition,
    requisitions = [],
    requisitionsIncluded = [],
    requisitionsIsLoading,
    requisitionsPagination,
    reviewRequisition,
    updateRequisition
  } = useRequisitions({
    limit,
    page,
    query: query?.toLowerCase() || undefined,
    status: status !== 'all' ? (status as any) : undefined,
    unit_id: unitId || undefined
  })

  const isOutboundForViewer = useCallback(
    (req: RequisitionData) => {
      const requestedById = req.relationships?.requested_by?.data?.id
      // Nurse list: all requests from the viewer's unit. Pharmacist tabs: only "mine" are outbound.
      if (isNurseView) {
        return currentUnitId
          ? req.relationships?.unit?.data?.id === currentUnitId
          : requestedById === `${currentStaffId}`
      }
      return requestedById === `${currentStaffId}`
    },
    [currentStaffId, currentUnitId, isNurseView]
  )

  const requisitionsByDirection = useMemo(() => {
    const outbound = requisitions.filter(isOutboundForViewer)
    const inbound = requisitions.filter(req => !isOutboundForViewer(req))
    return { inbound, outbound }
  }, [isOutboundForViewer, requisitions])

  const tableData = useMemo(
    () =>
      isNurseView
        ? requisitionsByDirection.outbound
        : tab === 'inbound'
          ? requisitionsByDirection.inbound
          : requisitionsByDirection.outbound,
    [isNurseView, requisitionsByDirection, tab]
  )

  const tabCounts = useMemo(
    () => ({ inbound: requisitionsByDirection.inbound.length, outbound: requisitionsByDirection.outbound.length }),
    [requisitionsByDirection]
  )

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: requisitions.length,
      pending_review: 0,
      approved: 0,
      partially_approved: 0,
      declined: 0,
      cancelled: 0
    }

    requisitions.forEach(requisition => {
      const currentStatus = requisition.attributes.status?.toLowerCase()
      if (currentStatus && Object.prototype.hasOwnProperty.call(counts, currentStatus)) {
        counts[currentStatus] += 1
      }
    })

    return counts
  }, [requisitions])

  const statusOptionsWithCounts = useMemo(
    () =>
      statusOptions.map(option => ({
        ...option,
        label: `${option.label}`
        // (${statusCounts[option.value] ?? 0})
      })),
    [statusCounts]
  )

  const unitOptions = useMemo(
    () => [
      { label: 'All Ward/Unit', value: '' },
      ...(unitList || []).map(unit => ({
        label: `${unit.attributes?.name || unit.id}${unit.attributes?.code ? ` (${unit.attributes.code})` : ''}`,
        value: unit.id
      }))
    ],
    [unitList]
  )

  const selectedUnitOption = useMemo(() => {
    const selectedValue = unitId || ''
    return (
      unitOptions.find(option => option.value === selectedValue) ||
      (selectedValue ? { label: selectedValue, value: selectedValue } : unitOptions[0] || null)
    )
  }, [unitId, unitOptions])

  const onFulfill = async (id: string) => {
    setFulfillingId(id)
    try {
      await fulfillRequisition(id)
      showToast('Requisition fulfilled successfully')
    } catch (error) {
      getError(error)
    } finally {
      setFulfillingId('')
    }
  }

  const onCancel = async (id: string) => {
    setCancellingId(id)
    try {
      await updateRequisition({
        id,
        payload: {
          data: {
            id,
            type: 'requisition',
            attributes: {
              status: 'cancelled'
            }
          }
        }
      })
      showToast('Requisition cancelled successfully')
    } catch (error) {
      getError(error)
    } finally {
      setCancellingId('')
    }
  }

  const columns = useMemo<ColumnDef<RequisitionData>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: ({ row }) => <Text size="14px">{row.original.id}</Text>,
        header: 'S/N'
      },
      {
        accessorKey: 'products',
        cell: ({ row }) => {
          const items = getRequisitionItems(row.original, requisitionsIncluded)
          return <Text size="14px">{productSummary(items)}</Text>
        },
        header: 'Products'
      },

      {
        accessorKey: 'unit',
        cell: ({ row }) => {
          const unitId = row.original.relationships?.unit?.data?.id
          const unit = getIncludedData(requisitionsIncluded, 'unit', unitId)
          const facility = getIncludedData(requisitionsIncluded, 'facility', unit?.relationships?.facility?.data?.id)
          const inventoryLocationId = row.original.relationships?.inventory_location?.data?.id
          const inventoryLocation = getIncludedData(requisitionsIncluded, 'inventory_location', inventoryLocationId)
          const inventoryLocationName = inventoryLocation?.attributes?.name
          const inventoryLocationFacility = getIncludedData(
            requisitionsIncluded,
            'facility',
            inventoryLocation?.relationships?.facility?.data?.id
          )
          const inventoryLocationFacilityName = inventoryLocationFacility?.attributes?.name
          return (
            <Text size="14px">
              {unitId
                ? unit?.attributes?.name + (facility?.attributes?.name ? ` (${facility?.attributes?.name})` : '') || '-'
                : inventoryLocationName +
                    (inventoryLocationFacilityName ? ` (${inventoryLocationFacilityName})` : '') || '-'}
            </Text>
          )
        },
        header: 'Ward/Facility'
      },
      {
        accessorKey: 'request_date',
        cell: ({ row }) => {
          const date = row.original.attributes.created_at
          return <Text size="14px">{date ? moment(date).format('MM/DD/YYYY') : '-'}</Text>
        },
        header: 'Request Date'
      },
      ...(tab === 'inbound'
        ? [
            {
              accessorKey: 'requested_by',
              cell: ({ row }: any) => {
                const staffId = row.original.relationships?.requested_by?.data?.id
                const staff = getIncludedData(requisitionsIncluded, 'staff', staffId)
                const first = staff?.attributes?.first_name || ''
                const last = staff?.attributes?.last_name || ''
                const name = `${first} ${last}`.trim()
                return <Text size="14px">{name || '-'}</Text>
              },
              header: 'Requested By'
            }
          ]
        : [
            {
              accessorKey: 'reviewed_by',
              cell: ({ row }: any) => {
                const staffId = row.original.relationships?.reviewed_by?.data?.id
                const staff = getIncludedData(requisitionsIncluded, 'staff', staffId)
                const first = staff?.attributes?.first_name || ''
                const last = staff?.attributes?.last_name || ''
                const name = `${first} ${last}`.trim()
                return <Text size="14px">{name || '-'}</Text>
              },
              header: 'Reviewed By'
            }
          ]),
      {
        accessorKey: 'status',
        cell: ({ row }) => (
          <Badge
            label={statusLabel(row.original.attributes.status)}
            size="small"
            status={row.original.attributes.status}
          />
        ),
        header: 'Status'
      },
      {
        accessorKey: 'actions',
        cell: ({ row }) => {
          const datum = row.original
          const requisitionStatus = datum.attributes.status?.toLowerCase()
          const isOutbound = isOutboundForViewer(datum)

          const getActions = () => {
            const actions = []

            actions.push({
              handleClick: (onClose: () => void) => {
                openModal(PHARMACY_MODAL_TYPE.REQUISITION_DETAILS, {
                  requisitionId: datum.id,
                  defaultData: datum,
                  included: requisitionsIncluded
                })
                onClose()
              },
              label: 'View details'
            })

            if (isOutbound) {
              if (requisitionStatus === 'pending_review' || requisitionStatus === 'pending') {
                actions.push({
                  handleClick: (onClose: () => void) => {
                    navigate(
                      isNurseView
                        ? `${APP_ROUTES_APP_NURSE_REQUISITIONS}/${datum.id}/edit`
                        : `${APP_ROUTES_APP_PHARMACIST_REQUISITIONS}/${datum.id}/edit`
                    )
                    onClose()
                  },
                  label: 'Edit request'
                })
                actions.push({
                  handleClick: (onClose: () => void) => {
                    onCancel(datum.id)
                    onClose()
                  },
                  label: cancellingId === datum.id ? 'Cancelling...' : 'Cancel'
                })
                return actions
              }

              // actions.push({
              //   handleClick: (onClose: () => void) => {
              //     window.print()
              //     onClose()
              //   },
              //   label: 'Print'
              // })
              return actions
            }

            if (requisitionStatus === 'cancelled') {
              // Only view details for cancelled
              return actions
            }

            if (requisitionStatus === 'pending_review' || requisitionStatus === 'pending') {
              actions.push({
                handleClick: (onClose: () => void) => {
                  openModal(PHARMACY_MODAL_TYPE.REQUISITION_ASSESS, {
                    requisitionId: datum.id,
                    defaultData: datum,
                    included: requisitionsIncluded
                  })
                  onClose()
                },
                label: 'Assess request'
              })
              return actions
            }

            if (requisitionStatus === 'approved') {
              // Approved: view details, print
              actions.unshift({
                handleClick: (onClose: () => void) => {
                  onFulfill(datum.id)
                  onClose()
                },
                label: fulfillingId === datum.id ? 'Fulfilling...' : 'Fulfill request'
              })
              // actions.push({
              //   handleClick: (onClose: () => void) => {
              //     window.print()
              //     onClose()
              //   },
              //   label: 'Print'
              // })
              return actions
            }

            if (requisitionStatus === 'partially_approved') {
              // Partially approved: fulfill request, view details, print
              actions.unshift({
                handleClick: (onClose: () => void) => {
                  onFulfill(datum.id)
                  onClose()
                },
                label: fulfillingId === datum.id ? 'Fulfilling...' : 'Fulfill request'
              })
              // actions.push({
              //   handleClick: (onClose: () => void) => {
              //     window.print()
              //     onClose()
              //   },
              //   label: 'Print'
              // })
              return actions
            }

            // Default case - just view details
            return actions
          }

          return (
            <div className="flex justify-end">
              <DropdownBtn dropdownLabel={<TriDot />} options={getActions()} />
            </div>
          )
        },
        header: ''
      }
    ],
    [
      cancellingId,
      fulfillingId,
      isNurseView,
      isOutboundForViewer,
      navigate,
      openModal,
      onCancel,
      onFulfill,
      requisitionsIncluded,
      tab
    ]
  )

  const statusDropdown = useMemo(
    () => (
      <DropdownBtn
        dropdownLabel={
          <Box align="center" direction="row" gap="4px" justify="center">
            <Text size="small" style={{ textTransform: 'capitalize' }}>
              {statusOptionsWithCounts.find(option => option.value === status)?.label || 'All Status'}
            </Text>
            <DropDownIcon />
          </Box>
        }
        options={statusOptionsWithCounts.map(option => ({
          activeBtn: status === option.value,
          handleClick: (onClose: () => void) => {
            setSearchParams(prev => {
              const params = new URLSearchParams(prev)
              if (option.value === 'all') params.delete('status')
              else params.set('status', option.value)
              params.set('page', '1')
              return params
            })
            onClose()
          },
          icon: status === option.value ? <Checkmark size="small" /> : <></>,
          label: option.label
        }))}
        style={{
          border: `1px solid rgba(0, 0, 0, 0.33)`,
          borderRadius: '100px',
          minWidth: '140px',
          padding: '6px 12px'
        }}
      />
    ),
    [setSearchParams, status, statusOptionsWithCounts]
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (status !== 'all') count += 1
    if (unitId) count += 1
    return count
  }, [status, unitId])

  return (
    <>
      <div className="flex flex-col gap-4 overflow-auto h-full">
        <TitleHeader
          btnLabel="Add New Request"
          onClick={() =>
            navigate(isNurseView ? APP_ROUTES_APP_REQUISITIONS_NEW : APP_ROUTES_APP_PHARMACIST_REQUISITIONS_NEW)
          }
          title="Requisitions"
        />

        {!isNurseView && (
          <Box className="border-b border-b-[#D9D9D9]" direction="row" gap="8px" margin={{ bottom: '8px' }}>
            {requisitionTabs.map(tabOption => (
              <Box
                align="center"
                direction="row"
                flex={false}
                gap="xsmall"
                key={tabOption.value}
                onClick={() => {
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev)
                    params.set('tab', tabOption.value)
                    params.set('page', '1')
                    return params
                  })
                }}
                pad={{ horizontal: '6px', vertical: '10px' }}
                style={{
                  borderBottom: tab === tabOption.value ? '1px solid #A79A46' : '1px solid transparent',
                  cursor: 'pointer'
                }}
              >
                <Text size="small" weight={tab === tabOption.value ? 600 : 400}>
                  {tabOption.label}
                </Text>
                <Badge
                  label={String(tabCounts[tabOption.value])}
                  size="small"
                  status="default"
                  style={{
                    background: '#F2F2F2',
                    color: '#666',
                    minWidth: 24,
                    padding: '2px 6px'
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between flex-wrap">
          <div className="flex flex-wrap items-center gap-3">
            {statusDropdown}

            <Box
              align="center"
              border={{ size: '1px' }}
              direction="row"
              gap="8px"
              pad={{ horizontal: '12px', vertical: '8px' }}
              round="100px"
              style={{ borderColor: activeFilterCount > 0 ? '#A79A46' : '#999999' }}
            >
              <FilterIcon />
              <Text size="14px">Filter</Text>
              {activeFilterCount > 0 ? (
                <Box align="center" background={'brand'} height={'20px'} justify="center" round="full" width={'20px'}>
                  <Text color={'white'} size="12px">
                    {activeFilterCount}
                  </Text>
                </Box>
              ) : null}
            </Box>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              className="px-4 py-2 w-full sm:w-[220px] border border-gray-300 rounded-[25px] focus:outline-none focus:border-primary"
              onChange={event => {
                const value = event.target.value
                setSearchParams(prev => {
                  const params = new URLSearchParams(prev)
                  if (value) params.set('query', value)
                  else params.delete('query')
                  params.set('page', '1')
                  return params
                })
              }}
              placeholder="Search for drugs"
              type="text"
              value={query}
            />
            {/* <Box style={{ minWidth: '220px' }}>
              <Select
                borderRadius={'25px'}
                defaultOptions
                getOptionLabel={({ label }: any) => label}
                getOptionValue={({ value }: any) => value}
                isDisabled={unitsIsLoading}
                onChange={(selectedOption: any) => {
                  const nextUnitId = selectedOption?.value || ''
                  setSearchParams(prev => {
                    const params = new URLSearchParams(prev)
                    if (nextUnitId) params.set('unit_id', nextUnitId)
                    else params.delete('unit_id')
                    params.set('page', '1')
                    return params
                  })
                }}
                options={unitOptions}
                placeholder="Ward/Unit"
                value={selectedUnitOption}
              />
            </Box> */}
          </div>
        </div>

        <Table2
          columns={columns}
          data={tableData}
          isLoading={requisitionsIsLoading}
          message={
            isNurseView
              ? 'No requisitions found'
              : `No ${tab === 'inbound' ? 'Inbound' : 'Outbound'} requisitions found`
          }
          pageCount={requisitionsPagination?.count || 0}
        />
      </div>
    </>
  )
}

export default Requisitions
